/**
 * CohortCallDisposition — the REPEAT (cohort) team's post-call form.
 *
 * ⚠️ WHY THIS IS A SEPARATE FILE FROM SoftphoneCall.js
 * SoftphoneCall.js's CallDispositionDialog is imported by BOTH MyChats
 * (new-revenue agents) and RepeatAgentView (cohort agents). Its 5-outcome
 * array (WON/HOT/WARM/COLD/DEAD) is therefore shared: editing it to suit
 * cohort would silently change the new-revenue team's form too. Shailendra
 * 2026-09-04 was explicit — do not change the options for the new revenue
 * team. So cohort gets its own popup and its own endpoint, and
 * SoftphoneCall.js is left byte-identical.
 *
 * WHY A DIFFERENT TAXONOMY: a cohort agent rings an EXISTING buyer. "Wants
 * rate / sample / details" is a new-lead question; a repeat buyer's real
 * answer is "I still have old stock" or "your price moved". With no option
 * that fit, cohort agents fell back to the legacy free-text form — 14,053
 * "Call back" rows in 30 days, ZERO with a follow-up date, the promise typed
 * as "cb" (5,798x) / "ni" (2,202x). Nothing could build a due-callback list,
 * so agents worked from memory.
 *
 * DESIGN: deliberately the SAME visual language as CallDispositionDialog —
 * same header, same clickable outcome cards, same amber date panel, same
 * submit button. Only the options differ (plus 3 group headers, because 8
 * unlabelled cards would read as a wall, and a price panel on PRICE_ISSUE).
 *
 * Backend: POST {AI_PATH}/api/ai/voice/cohort-outcome
 *          GET  {AI_PATH}/api/ai/voice/cohort-outcomes  (taxonomy, server-owned)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { preopenFollowUpWa } from '../utils/whatsappFollowUp';
import {
  Button, Dialog, DialogContent, DialogActions, TextField, CircularProgress,
  Box, Typography, Collapse, Select, MenuItem, FormControl, InputLabel, InputAdornment,
} from '@material-ui/core';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EventIcon from '@material-ui/icons/Event';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import Inventory2Icon from '@material-ui/icons/Archive';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import BlockIcon from '@material-ui/icons/Block';
import PhoneMissedIcon from '@material-ui/icons/PhoneMissed';
import SendIcon from '@material-ui/icons/Send';

// Outcomes that describe what the buyer SAID — impossible on a call that never
// connected. Mirrors CONVERSATION_OUTCOMES in services/ai-service/cohortOutcome.js.
const CONVERSATION_OUTCOMES = new Set([
  'ORDER_PLACED', 'WILL_PLACE_LATER_HOT', 'WILL_PLACE_LATER', 'OLD_STOCK',
  'PRICE_ISSUE', 'COMPLAINT_RATE_SCHEME', 'NO_NEED_UNKNOWN', 'DENY_CONTINUE',
]);

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const API_PATH = (process.env.REACT_APP_PATH || '').trim();

// Presentation only — the taxonomy itself (labels, groups, which need a date)
// is SERVER-OWNED and fetched from /voice/cohort-outcomes, so the form and the
// validator can never drift apart. This map just gives each code an icon and a
// colour, and a one-line sub so the agent picks the right card at speed.
const VISUAL = {
  ORDER_PLACED:          { Icon: CheckCircleIcon,   color: '#0f7a37', bg: '#e7f7ec', sub: 'They ordered on this call.' },
  // HOT and WARM share the calendar icon but not the colour — HOT is the
  // strongest red on the form so it reads as the urgent card in its group.
  WILL_PLACE_LATER_HOT:  { Icon: EventIcon,         color: '#c5221f', bg: '#fdeaea', sub: 'Closing soon — set the date they agreed (≈3 days).' },
  WILL_PLACE_LATER:      { Icon: EventIcon,         color: '#d1471a', bg: '#fdeee7', sub: 'They will order later — set the date they agreed.' },
  OLD_STOCK:             { Icon: Inventory2Icon,    color: '#b8860b', bg: '#fdf4e0', sub: 'Stock still with them — set when YOU should check back.' },
  PRICE_ISSUE:           { Icon: LocalOfferIcon,    color: '#7b3fb5', bg: '#f4ecfc', sub: 'Wants a better rate — capture what they asked for.' },
  COMPLAINT_RATE_SCHEME: { Icon: ReportProblemIcon, color: '#c2185b', bg: '#fdeaf1', sub: 'Unhappy about a previous order’s rate or scheme.' },
  NO_NEED_UNKNOWN:       { Icon: HelpOutlineIcon,   color: '#1976d2', bg: '#e8f2fd', sub: 'Says no need, no clear reason given.' },
  DENY_CONTINUE:         { Icon: BlockIcon,         color: '#5d6d7e', bg: '#eef1f4', sub: 'No order coming — pick why, or that they would not say.' },
  NOT_CONTACTING:        { Icon: PhoneMissedIcon,   color: '#78909c', bg: '#eff3f5', sub: 'Not picking up / not responding.' },
};

const GROUP_ORDER = ['CONVERTED', 'WILL_LATER', 'NOT_CONVERTED'];
// Each group reads as a coloured BAND, not a grey caption — the agent should be
// able to aim at the right third of the form before reading a single label.
const GROUP_STYLE = {
  CONVERTED:     { tint: '#0f7a37', bg: '#e7f7ec', bar: '#0f7a37', note: 'Money today' },
  WILL_LATER:    { tint: '#c05517', bg: '#fdf1e7', bar: '#e07b2a', note: 'Order coming — date it' },
  NOT_CONVERTED: { tint: '#5d6d7e', bg: '#eef1f4', bar: '#90a4ae', note: 'No order in sight' },
};

const ENSURE_CATEGORIES = ['Diaper', 'No Gas Deo'];

// Mirror of the server's remark rules (cohortOutcome.js is the AUTHORITY — this
// only spares the agent a round-trip to be told the same thing). Measured over
// 60 days, the top remarks on these outcomes were "cb" (96), "call back" (55),
// "busy" (26), "ringing" (25): descriptions of the call, not the buyer.
const REMARK_BLOCKLIST = new Set([
  'cb', 'ccb', 'cbb', 'callback', 'callbak', 'calback', 'cback', 'clbk', 'cbk',
  'busy', 'bsy', 'busybusy', 'numberbusy', 'nobusy', 'linebusy',
  'ring', 'ringing', 'rining', 'ringng', 'noring', 'ringno',
  'notanswer', 'notanswering', 'noanswer', 'noans', 'nans', 'notpick',
  'notpicking', 'notpickup', 'nopickup', 'notrecive', 'notreceived',
  'callcut', 'cutcall', 'calldisconnect', 'calldiscconect', 'disconnect',
  'switchoff', 'switchedoff', 'swithoff', 'off', 'notreachable', 'unreachable',
  'nr', 'na', 'nc', 'nocontact', 'notcontacting', 'notconnected',
  'no', 'noo', 'nono', 'yes', 'ok', 'okk', 'okay', 'done', 'nothing', 'none',
  'ni', 'notinterested', 'nointerest', 'interested', 'nt', 'nil', 'null',
  'noneed', 'need', 'noneedd', 'norequirement', 'norqmnt',
  'followup', 'follow', 'fu', 'flwup', 'later', 'baadme', 'baadma', 'badma',
  'stock', 'stockh', 'stockhai', 'oldstock', 'hai', 'nahi', 'nhi', 'h',
  'test', 'testing', 'abc', 'xyz', 'same', 'asusual', 'pending',
]);
const KEYBOARD_RUNS = [
  'qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890',
  'qazwsxedc', 'poiuytrewq', 'lkjhgfdsa', 'mnbvcxz',
];
// Returns a SPECIFIC complaint, or null. Each branch names the rule that was
// broken — a generic "not readable" leaves the agent guessing which of four
// things is wrong on a form they fill in seconds between calls.
const remarkComplaint = (raw) => {
  const t = String(raw || '').trim();
  const letters = t.toLowerCase().replace(/[^a-z]/g, '');
  if (!letters) return null;
  if (!/\s/.test(t.replace(/[.,!?-]/g, ' ').trim())) {
    return 'One word is not enough — write a short sentence about what the buyer said.';
  }
  for (const row of KEYBOARD_RUNS) {
    for (let i = 0; i + 3 <= row.length; i += 1) {
      const run = row.slice(i, i + 3);
      if (letters.includes(run) || letters.includes([...run].reverse().join(''))) {
        return 'That looks like random typing. Write what the buyer actually said.';
      }
    }
  }
  if (/(.)\1{4,}/.test(letters)) {
    return 'That is the same letter repeated. Write what the buyer actually said.';
  }
  if (letters.length >= 6 && !/[aeiou]/.test(letters)) {
    return 'That does not read as words. Write what the buyer actually said.';
  }
  return null;
};
const remarkProblem = (raw, min) => {
  const t = String(raw || '').trim();
  // Nothing typed yet: say what is wanted, not what is wrong.
  if (!t) return `Write what the buyer said — at least ${min} characters.`;
  if (REMARK_BLOCKLIST.has(t.toLowerCase().replace(/[^a-z0-9]/g, ''))) {
    return `"${t}" describes the CALL, not the buyer. Write what they said.`;
  }
  const c = remarkComplaint(t);
  if (c) return c;
  // Length is checked LAST, so a genuine short note gets the honest reason
  // ("6 more characters") rather than being called gibberish.
  if (t.length < min) {
    const n = min - t.length;
    return `${n} more character${n === 1 ? '' : 's'} needed (${t.length}/${min}).`;
  }
  return null;
};

// Quick-pick call-back days. Typing a datetime is the single slowest thing in
// this form and the date is REQUIRED on the two most-used outcomes, so the
// common cases are one tap. "+7d" etc. resolve to 11:00 IST on that day — a
// sane calling hour, not the current minute-of-day.
const QUICK_DAYS = [
  { d: 2,  label: '2d' },
  { d: 3,  label: '3d' },
  { d: 7,  label: '1 wk' },
  { d: 15, label: '15d' },
  { d: 30, label: '1 mo' },
];

// ⚠️ THE CHIPS MUST MATCH THE PERMISSIBLE WINDOW (Shailendra 2026-09-22:
// "currently 1 month option is able being shown which doesnt match the
// permissisble value"). One global list offered "1 mo" on a HOT lead that must
// convert in 3 days — the form invited the exact date the server then rejects.
// So the chips are derived from the outcome's own window: HOT shows 1d/2d/3d,
// WARM shows 4d..10d, and an outcome with no window keeps the full list.
const quickDaysFor = (win) => {
  if (!win) return QUICK_DAYS;
  const out = [];
  for (let d = win.minDays; d <= win.maxDays; d += 1) {
    if (d === 7) out.push({ d, label: '1 wk' });
    else out.push({ d, label: `${d}d` });
  }
  // WARM spans 4-10 — seven chips is a wall, so thin it to the useful ones.
  return out.length > 5
    ? out.filter((x) => [win.minDays, win.minDays + 1, 7, win.maxDays - 1, win.maxDays].includes(x.d))
    : out;
};
// datetime-local wants local wall-clock, so offset before slicing.
const localInputValue = (dt) =>
  new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  d.setHours(11, 0, 0, 0);
  return localInputValue(d);
};

const fmtDur = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * @param dispo { open, phone, talktime, name? }
 * @param onClose called after a successful log (or cancel-equivalent)
 */
/**
 * `afterSubmit` runs once the outcome is SAVED, before onClose. It exists for
 * the predictive path: posting the outcome does not free Phonomenal's session,
 * and only ending that session lets the engine ring the agent again — so the
 * predictive mount passes a callback that clears it. Without this a cohort
 * agent on predictive would log the call and then sit idle forever
 * (see /voice/predictive/session/dispose in routes.js).
 */
// `redial` (optional, 2026-10-15): { onClick, busy, error } — the predictive
// cockpit's "↻ Redial" for a call that got cut by mistake. Lives inside the
// dialog because the dialog's backdrop covers anything rendered behind it.
export default function CohortCallDisposition({ dispo, onClose, afterSubmit, redial }) {
  // `dispo.previewHistory` lets the local preview page show the "last call"
  // panel without pointing at a real buyer. Nothing in the live call paths ever
  // sets it, so production behaviour is unchanged — the server's answer always
  // wins if one arrives.
  const [taxonomy, setTaxonomy] = useState([]);
  const [groups, setGroups] = useState({});
  const [outcome, setOutcome] = useState('');
  const [nextCallAt, setNextCallAt] = useState('');
  const [remark, setRemark] = useState('');
  const [priceAsk, setPriceAsk] = useState('');
  const [priceQty, setPriceQty] = useState('');
  const [priceCategory, setPriceCategory] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // The server's verdict on whether THIS call connected. null = not yet known
  // (or no telephony row found), in which case the form stays permissive and
  // the submit-time guard is the backstop.
  const [connected, setConnected] = useState(null);
  // The REAL talk time from the telephony record, once known. Shown in place of
  // the softphone's wall-clock figure, which counts ring time.
  const [realTalktime, setRealTalktime] = useState(null);
  const [checkingCall, setCheckingCall] = useState(false);
  // What this buyer said LAST time — served on the same call-connected lookup,
  // so the form still makes one request when it opens.
  const [history, setHistory] = useState(null);
  const lastPhoneRef = useRef('');
  // Scrolls the chosen card (and its now-open date panel) into view, so picking
  // the most-used outcome never leaves its required field off-screen.
  const selectedRef = useRef(null);

  // Ask the server whether this call connected. The webhook that writes
  // phonomenalcalllogs can lag the hang-up by a few seconds, so this is also
  // wired to a manual "Recheck" button — without it the form would sit on the
  // softphone's misleading ring-time figure until the agent reopened it
  // (Shailendra 2026-09-14).
  // ⚠️ ASK BY PHONE, not sessionId. Phonomenal's sessionId is the AGENT's
  // session, shared across every call on it, so the server cannot identify THIS
  // call from it — it returned "unknown" and the form opened with every option
  // on a dead call (Shailendra 2026-09-16). The server matches the caller's
  // most recent call to this number instead.
  const checkConnected = (phone, guardPhone) => {
    if (!phone) return;
    setCheckingCall(true);
    // callId is set ONLY by DispositionGate (an older pending call) — reads that
    // exact call; every existing caller leaves it unset, so nothing changes there.
    axios.get(AI_PATH + '/api/ai/voice/call-connected', { params: { phone, callId: dispo?.callId || undefined } })
      .then((r) => {
        if (guardPhone && lastPhoneRef.current !== guardPhone) return;
        // History arrives even when there is no telephony row (a first call
        // from a new extension), so read it BEFORE the known-check returns.
        setHistory(r.data?.history || dispo?.previewHistory || null);
        if (!r.data?.known) return;
        setConnected(!!r.data.connected);
        setRealTalktime(Number(r.data.talktime) || 0);
        // ── NOBODY ANSWERED: JUST CLOSE ───────────────────────────────────
        // Nothing to decide and nothing to write. The dial is ALREADY on the
        // record in phonomenalcalllogs with talktime 0, and every report
        // derives "not connected" from that — writing NOT_CONTACTING would only
        // restate a fact the telephony data already proves, at the cost of an
        // API round-trip (Shailendra 2026-09-14: "its obvius that its not
        // contacting...y to hit the api").
        // So the form gets out of the way and the agent moves to the next dial;
        // onClose is what advances the dialler.
        if (!r.data.connected) onClose();
      })
      .catch(() => { /* unknown — stay permissive; the server still guards */ })
      .finally(() => setCheckingCall(false));
  };

  // Taxonomy is server-owned (see the file header), so a failed fetch leaves
  // the agent with a form that has NO OUTCOMES AT ALL — the dialog renders its
  // header and submit button over empty space, with nothing explaining why.
  //
  // ⚠️ THIS IS NOT COSMETIC. It happened live on 2026-09-23: the API was down,
  // the single on-mount fetch failed, .catch() swallowed it, and the form stayed
  // permanently blank even after the API came back — because nothing ever
  // retried. An agent hitting this mid-shift cannot log a call and has no idea
  // why. So: retry with backoff, re-fetch whenever the dialog reopens, and SAY
  // SO on screen when it still fails.
  const [taxoError, setTaxoError] = useState(false);
  // The talk-time rule, server-owned like the taxonomy itself.
  const [engagedSec, setEngagedSec] = useState(25);
  const [nonEngageSubs, setNonEngageSubs] = useState([]);
  const loadTaxonomy = useCallback((attempt = 0) => {
    axios.get(AI_PATH + '/api/ai/voice/cohort-outcomes')
      .then((r) => {
        const list = r.data?.outcomes || [];
        setTaxonomy(list);
        setGroups(r.data?.groups || {});
        if (r.data?.engagedTalktimeSec) setEngagedSec(r.data.engagedTalktimeSec);
        setNonEngageSubs(r.data?.nonEngagementSubs || []);
        setTaxoError(!list.length);
      })
      .catch(() => {
        // 4 tries over ~15s covers a backend restart without hammering it.
        if (attempt < 4) setTimeout(() => loadTaxonomy(attempt + 1), 1000 * 2 ** attempt);
        else { setTaxonomy([]); setTaxoError(true); }
      });
  }, []);
  useEffect(() => { loadTaxonomy(0); }, [loadTaxonomy]);
  // Re-fetch on every open: a dialog mounted once at login would otherwise hold
  // a failed fetch for the whole shift.
  useEffect(() => { if (dispo?.open && !taxonomy.length) loadTaxonomy(0); }, [dispo?.open, taxonomy.length, loadTaxonomy]);

  useEffect(() => {
    const p10 = String(dispo?.phone || '').replace(/\D/g, '').slice(-10);
    if (dispo?.open && p10 && p10 !== lastPhoneRef.current) {
      lastPhoneRef.current = p10;
      setOutcome(''); setNextCallAt(''); setRemark(''); setError('');
      setPriceAsk(''); setPriceQty(''); setPriceCategory(''); setSubStatus('');
      // Did the call actually connect? The softphone's talktime counts RING
      // time, so it cannot answer this — the telephony record can.
      setConnected(null); setRealTalktime(null);
      setHistory(dispo?.previewHistory || null);
      checkConnected(dispo?.phone, p10);
      axios.post(API_PATH + '/sales/callDropdowns?filterType=productCategories')
        .then((r) => {
          if (lastPhoneRef.current !== p10) return;
          const merged = [...(r.data?.data || [])];
          ENSURE_CATEGORIES.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
          setCategoryOptions(merged);
        })
        .catch(() => setCategoryOptions([...ENSURE_CATEGORIES]));
    }
  }, [dispo?.open, dispo?.phone]);

  // A sub-status belongs to ONE outcome, so switching outcome must clear it —
  // otherwise a code from the previous pick rides along and fails validation
  // (or worse, silently mislabels the lead).
  useEffect(() => { setSubStatus(''); }, [outcome]);

  useEffect(() => {
    if (!outcome || !selectedRef.current) return;
    const t = setTimeout(() => {
      try { selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch { /* older browsers */ }
    }, 190);   // after the Collapse has expanded, or we scroll to the wrong height
    return () => clearTimeout(t);
  }, [outcome]);

  const meta = taxonomy.find((o) => o.value === outcome) || null;
  const vis = outcome ? VISUAL[outcome] : null;
  const accent = vis?.color || '#1565c0';

  const needsDate = meta?.dateMode === 'required';
  const showDate = !!meta && meta.dateMode !== 'none';
  const dateMissing = needsDate && !nextCallAt;
  const isPrice = outcome === 'PRICE_ISSUE';
  const priceMissing = isPrice && (!priceCategory || !(Number(priceAsk) > 0) || !(Number(priceQty) > 0));

  const remarkMin = meta?.remarkMin || 15;
  const remarkNeeded = !!meta?.remarkRequired;
  const remarkIssue = remarkNeeded ? remarkProblem(remark, remarkMin) : null;
  const subOptions = meta?.subStatuses || null;
  const subMissing = !!subOptions && !subStatus;
  const dateWindow = meta?.dateWindow || null;

  // The SOFT FLOOR. Measured over 60 days, 75% of WARM follow-ups are already
  // set at <=3 days, so blocking them would not tidy the edges — it would push
  // agents to pick HOT just to keep the date they agreed. Warn, let them
  // through (Shailendra 2026-09-22: "warning not hard block").
  const daysOut = nextCallAt ? (new Date(nextCallAt).getTime() - Date.now()) / 86400000 : null;
  const belowFloor = !!dateWindow && daysOut !== null
    && !dateWindow.hardMin && daysOut < dateWindow.minDays;
  const aboveCeiling = !!dateWindow && daysOut !== null && daysOut > dateWindow.maxDays;

  const submitDisabled = submitting || !outcome || dateMissing || priceMissing
    || subMissing || aboveCeiling || !!remarkIssue;

  const submit = async () => {
    setSubmitting(true); setError('');
    // HOT / WARM → open WhatsApp to the buyer with the agent's intro pre-typed.
    const wa = preopenFollowUpWa(dispo?.phone,
      outcome === 'WILL_PLACE_LATER_HOT' || outcome === 'WILL_PLACE_LATER');
    try {
      await axios.post(AI_PATH + '/api/ai/voice/cohort-outcome', {
        phone: dispo?.phone,
        outcome,
        subStatus: subStatus || undefined,
        nextCallAt: nextCallAt ? new Date(nextCallAt).toISOString() : undefined,
        remark: remark || undefined,
        talktime: dispo?.talktime,
        // Lets the server read the REAL talktime from phonomenalcalllogs rather
        // than trusting the wall-clock figure computed here, which counts ring
        // time on a call that was never answered.
        sessionId: dispo?.sessionId,
        priceAsk: isPrice ? Number(priceAsk) : undefined,
        priceQty: isPrice ? Number(priceQty) : undefined,
        priceCategory: isPrice ? priceCategory : undefined,
      });
      // Side effects belong to the CALLER (release the predictive session,
      // advance the progressive queue). A failure here must not look like a
      // failed save — the outcome is already written.
      if (typeof afterSubmit === 'function') {
        try { await afterSubmit(); } catch (err) { /* caller re-polls */ }
      }
      wa.go();
      onClose();
    } catch (e) {
      wa.cancel();
      // Show the reason rather than closing quietly: unlike the new-rev form,
      // a rejection here means the PROMISE wasn't saved, and a lost promise is
      // exactly the failure this whole form exists to stop.
      setError(e.response?.data?.message || 'Could not save. Please try again.');
    } finally { setSubmitting(false); }
  };

  const minLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  return (
    <Dialog
      open={!!dispo?.open} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown
      // Same fix as CallDispositionDialog in SoftphoneCall.js — must render above any
      // full-screen lock overlay (e.g. RtoRiskLockPopup, 1300) or the agent gets stuck unable
      // to complete the post-call wrap-up.
      style={{ zIndex: 1500 }}
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
    >
      <Box style={{ height: 5, background: accent, transition: 'background 150ms ease' }} />

      {/* Tinted header — picks up the selected outcome's colour so the whole
          dialog confirms the choice, instead of staying white. */}
      <Box style={{
        padding: '16px 20px 12px',
        background: vis ? `linear-gradient(180deg, ${vis.bg} 0%, #ffffff 100%)` : 'linear-gradient(180deg, #f4f7fb 0%, #ffffff 100%)',
        transition: 'background 180ms ease',
      }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 0.6 }}>
            CALL ENDED
          </Typography>
          <Box style={{
            padding: '2px 9px', borderRadius: 999,
            background: 'linear-gradient(90deg,#3b6ea5,#5b8fc7)', color: '#fff',
            fontSize: 9.5, fontWeight: 900, letterSpacing: 0.6,
            boxShadow: '0 1px 3px rgba(59,110,165,0.35)',
          }}>
            REPEAT BUYER
          </Box>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 4 }}>
          <Typography style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
            {dispo?.phone}
          </Typography>
          {/* Once the telephony record is known it REPLACES the softphone's
              figure — that one counts ring time, so an unanswered call shows
              "9s" of talk that never happened. Red when the call never
              connected, so the agent sees why the options are limited. */}
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999,
            background: connected === false ? '#fdeaea' : '#f1f5f9',
            color: connected === false ? '#a8202b' : '#475569',
            fontSize: 12, fontWeight: 700,
          }}>
            <HeadsetMicIcon style={{ fontSize: 13 }} />
            {fmtDur(realTalktime !== null ? realTalktime : dispo?.talktime)}
            {connected === false && ' · no answer'}
          </Box>
          {/* The webhook that writes the call log can lag the hang-up. Without
              this the form would sit on the misleading ring-time figure until
              the agent reopened it (Shailendra 2026-09-14). */}
          {dispo?.phone && connected === null && (
            <Box
              onClick={() => !checkingCall && checkConnected(dispo.phone)}
              style={{
                padding: '2px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                background: '#eef3f8', color: '#1565c0',
                cursor: checkingCall ? 'default' : 'pointer', opacity: checkingCall ? 0.6 : 1,
              }}
            >
              {checkingCall ? 'Checking…' : '↻ Recheck call'}
            </Box>
          )}
        </Box>
        {dispo?.name && (
          <Typography style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{dispo.name}</Typography>
        )}
      </Box>

      {/* The outcome list scrolls; the submit button never does. Before this the
          date field and the button both sat below 8 cards, so the most-used
          outcome needed a scroll just to reach its own required field. */}
      <DialogContent style={{ paddingTop: 10, maxHeight: '58vh', overflowY: 'auto' }}>
        {/* ── WHAT THEY SAID LAST TIME ──────────────────────────────────────
            First thing on the form, because on a cohort book the previous call
            IS the reason for this one. A buyer who said "PO shared, confirming
            tomorrow" needs a different opening line from one who said "stock
            abhi bacha hai" — and the agent has no memory of either
            (Shailendra 2026-09-23). An OVERDUE promise is called out in red:
            586 cohort call-backs were already past their date when this shipped. */}
        {!!history && (
          <Box style={{
            marginBottom: 12, padding: '9px 11px', borderRadius: 9,
            background: history.overdue ? '#fff4e5' : '#f5f8fc',
            border: `1px solid ${history.overdue ? '#f0c27b' : '#dbe3ea'}`,
          }}>
            <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
              <Typography style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', letterSpacing: 0.4 }}>
                LAST CALL
              </Typography>
              {!!history.at && (
                <Typography style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600 }}>
                  {new Date(history.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  {history.by ? ` · ${history.by}` : ''}
                  {history.callCount ? ` · call ${history.callCount}` : ''}
                </Typography>
              )}
            </Box>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: '#334155' }}>
              {history.outcomeLabel}
              {history.subStatusLabel && (
                <span style={{ fontWeight: 600, color: '#64748b' }}> · {history.subStatusLabel}</span>
              )}
            </Typography>
            {/* The agent's own words are the useful part — shown verbatim,
                quoted, so it reads as a note and not as another field. */}
            {!!history.remark && (
              <Typography style={{ fontSize: 12, color: '#475569', marginTop: 3, fontStyle: 'italic' }}>
                &ldquo;{history.remark}&rdquo;
              </Typography>
            )}
            {!!history.nextCallAt && (
              <Typography style={{
                fontSize: 11.5, marginTop: 5, fontWeight: 800,
                color: history.overdue ? '#b45309' : '#0f7a37',
              }}>
                {history.overdue ? '⚠ Call-back was due ' : 'Call-back promised for '}
                {new Date(history.nextCallAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </Typography>
            )}
          </Box>
        )}

        <Typography style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 0.4, marginBottom: 8 }}>
          HOW DID IT GO?
        </Typography>

        {/* Says WHY the list is short, so a missing option reads as intentional
            rather than as a broken form. */}
        {connected === false && (
          <Box style={{
            margin: '0 0 10px', padding: '8px 11px', borderRadius: 8,
            background: '#fdeaea', border: '1px solid #f3c9c9',
            fontSize: 11.5, color: '#a8202b', fontWeight: 600, lineHeight: 1.5,
          }}>
            This call never connected — the phone rang but nobody answered.
            Only the non-contact options are available.
          </Box>
        )}

        {/* The form is useless without the taxonomy — say so rather than
            presenting empty space the agent cannot act on. */}
        {!taxonomy.length && (
          <Box style={{
            padding: '14px 12px', borderRadius: 9, textAlign: 'center',
            background: taxoError ? '#fdecea' : '#f5f7fa',
            border: `1px solid ${taxoError ? '#f5b0ab' : '#e2e8f0'}`,
          }}>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: taxoError ? '#b71c1c' : '#64748b' }}>
              {taxoError ? 'Could not load the outcome list' : 'Loading outcomes…'}
            </Typography>
            {taxoError && (
              <>
                <Typography style={{ fontSize: 11, color: '#8a3b36', marginTop: 4 }}>
                  The server did not respond. Your call is already recorded — retry to log the outcome.
                </Typography>
                <Box
                  onClick={() => { setTaxoError(false); loadTaxonomy(0); }}
                  style={{
                    display: 'inline-block', marginTop: 9, padding: '6px 16px', borderRadius: 999,
                    background: '#c62828', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  Retry
                </Box>
              </>
            )}
          </Box>
        )}

        {GROUP_ORDER.map((g) => {
          const items = taxonomy.filter((o) => o.group === g);
          if (!items.length) return null;
          const gs = GROUP_STYLE[g] || {};
          return (
            <Box key={g} style={{ marginBottom: 10 }}>
              {/* Group band — colour carries the meaning at rest, so the agent
                  aims at a third of the form before reading any label. */}
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: gs.bg, borderLeft: `4px solid ${gs.bar}`,
                borderRadius: 6, padding: '5px 10px', margin: '0 0 8px',
              }}>
                <Typography style={{ fontSize: 10.5, fontWeight: 900, color: gs.tint, letterSpacing: 0.8 }}>
                  {groups[g] || g}
                </Typography>
                <Typography style={{ fontSize: 10, color: gs.tint, opacity: 0.75, fontWeight: 600 }}>
                  · {gs.note}
                </Typography>
              </Box>
              {items.filter((o) => {
                // Gate BOTH ways, on the server's verdict rather than the
                // softphone's wall-clock timer (which counts ring time):
                //   connected === true  -> hide "Not Contacting". It is
                //     meaningless once you have spoken; it accounted for 1,276
                //     reached buyers, 678 after 60+ seconds of talk
                //     (Shailendra 2026-09-13).
                //   connected === false -> hide every CONVERSATION outcome. A
                //     call that only rang cannot produce a statement about what
                //     the buyer said; one such call was logged OLD_STOCK on
                //     2026-09-14, which queues a call-back weeks out and tells
                //     every report the buyer is covered.
                //   connected === null  -> unknown, show everything; the server
                //     still rejects an impossible pick on submit.
                if (o.value === 'NOT_CONTACTING') return connected !== true;
                if (connected === false) return !CONVERSATION_OUTCOMES.has(o.value);
                return true;
              }).map((o) => {
                const selected = outcome === o.value;
                const v = VISUAL[o.value] || {};
                const Icon = v.Icon || HelpOutlineIcon;
                const color = v.color || '#1565c0';
                return (
                  // Outer = the card (holds the row AND, when selected, its own
                  // inline date panel). Inner row keeps the original flex layout.
                  <Box
                    key={o.value}
                    ref={selected ? selectedRef : null}
                    onClick={() => { setOutcome(o.value); setError(''); }}
                    style={{
                      padding: '10px 12px', marginBottom: 7,
                      borderRadius: 10, cursor: 'pointer',
                      // At REST the card already carries its colour: tinted left
                      // edge + coloured icon on its own tint. Selection then
                      // deepens it. Previously everything was grey until clicked,
                      // which made a form used 40x/day read as dead.
                      border: `1.5px solid ${selected ? color : '#e8ebef'}`,
                      borderLeft: `4px solid ${color}`,
                      background: selected ? v.bg : '#fff',
                      boxShadow: selected ? `0 2px 8px ${color}26` : '0 1px 2px rgba(16,24,40,0.04)',
                      transition: 'border-color 120ms ease, background 120ms ease, box-shadow 120ms ease',
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                    <Box style={{
                      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: selected ? color : v.bg,
                      color: selected ? '#fff' : color,
                      transition: 'background 120ms ease, color 120ms ease',
                    }}>
                      <Icon style={{ fontSize: 19 }} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: 13, fontWeight: 800, color: selected ? color : '#1f2937', lineHeight: 1.3 }}>
                        {o.label}
                      </Typography>
                      <Typography style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.4, marginTop: 1 }}>
                        {v.sub}
                      </Typography>
                      {/* Says the date is coming BEFORE the card is picked, so a
                          required date never feels like a surprise tax. */}
                      {o.dateMode === 'required' && (
                        <Typography style={{ fontSize: 9.5, fontWeight: 800, color, letterSpacing: 0.3, marginTop: 3 }}>
                          📅 NEEDS A DATE
                        </Typography>
                      )}
                    </Box>
                    <Box style={{
                      width: 19, height: 19, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                      border: `2px solid ${selected ? color : '#cbd5e1'}`,
                      background: selected ? color : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'border-color 120ms ease, background 120ms ease',
                    }}>
                      {selected && <Box style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                    </Box>
                    </Box>

                    {/* Date lives INSIDE the chosen card. It used to sit below all
                        8 cards, so the most-used outcome (Follow-up / Old Stock)
                        forced a scroll past everything to reach its own required
                        field. Quick-pick chips mean the usual case is one tap and
                        no typing at all. */}
                    <Collapse in={selected} timeout={160}>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          marginTop: 10, padding: '10px 11px', borderRadius: 9,
                          background: (dateMissing || priceMissing || subMissing) ? '#fffaf0' : '#fff',
                          border: `1px dashed ${(dateMissing || priceMissing || subMissing) ? '#e0a53b' : '#cbd5e1'}`,
                        }}
                      >
                        {/* THE EXACT STATUS. Sits above the date because it is the
                            first question the agent can answer, and because on the
                            NI outcomes it is the only question — those have no date
                            panel at all and the field would otherwise be stranded
                            under a free-text box nobody reads. */}
                        {!!o.subStatuses && (
                          <Box style={{ marginBottom: o.dateMode !== 'none' ? 12 : 0 }}>
                            <Typography style={{ fontSize: 11.5, fontWeight: 800, color: '#374151', marginBottom: 7 }}>
                              {/* NO_NEED_UNKNOWN asks HOW FAR THE CALL GOT, not why —
                                  the outcome means the buyer gave no reason, so
                                  "Why exactly?" would demand the thing it says is
                                  missing. Every other NOT_CONVERTED outcome does
                                  have a reason to give. */}
                              {o.group === 'NOT_CONVERTED' ? 'Why exactly?' : 'Where has it reached?'}
                              <span style={{ color: '#c62828' }}> *</span>
                            </Typography>
                            <FormControl fullWidth size="small" variant="outlined" style={{ background: '#fff' }}>
                              <InputLabel>Select the exact status</InputLabel>
                              <Select
                                label="Select the exact status"
                                value={subStatus}
                                onChange={(e) => setSubStatus(e.target.value)}
                                MenuProps={{ style: { zIndex: 1600 } }}
                              >
                                {o.subStatuses.map((x) => {
                                  // ⚠️ "They cut the call" is not available after a
                                  // real conversation. Measured over 14 days, 54% of
                                  // "no reason given" followed 20+ seconds of talk —
                                  // past that the agent learned something, and the
                                  // non-engagement options are simply not true.
                                  // Greyed rather than hidden, so the rule is visible.
                                  const blocked = nonEngageSubs.includes(x.value)
                                    && realTalktime !== null && realTalktime > engagedSec;
                                  return (
                                    <MenuItem key={x.value} value={x.value} disabled={blocked}
                                      style={{ whiteSpace: 'normal' }}>
                                      {x.label}
                                      {blocked && (
                                        <span style={{ fontSize: 10.5, color: '#b45309', marginLeft: 6 }}>
                                          — you spoke for {realTalktime}s
                                        </span>
                                      )}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                        {o.dateMode !== 'none' && (
                        <>
                        <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 7 }}>
                          <EventIcon style={{ fontSize: 15, color }} />
                          <Typography style={{ fontSize: 11.5, fontWeight: 800, color: '#374151' }}>
                            {o.value === 'OLD_STOCK' ? 'When should you check back?' : 'When should we call back?'}
                            {o.dateMode === 'required'
                              ? <span style={{ color: '#c62828' }}> *</span>
                              : <span style={{ color: '#9ca3af', fontWeight: 600 }}> (optional)</span>}
                          </Typography>
                        </Box>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginBottom: 8 }}>
                          {quickDaysFor(o.dateWindow).map((q) => {
                            const val = inDays(q.d);
                            const on = nextCallAt === val;
                            return (
                              <Box
                                key={q.d}
                                onClick={() => setNextCallAt(val)}
                                style={{
                                  padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                                  fontSize: 12, fontWeight: 800, userSelect: 'none',
                                  background: on ? color : '#f1f5f9',
                                  color: on ? '#fff' : '#475569',
                                  border: `1px solid ${on ? color : '#dbe3ea'}`,
                                  transition: 'background 110ms ease, color 110ms ease',
                                }}
                              >
                                {q.label}
                              </Box>
                            );
                          })}
                        </Box>
                        <TextField
                          fullWidth size="small" type="datetime-local" variant="outlined"
                          value={nextCallAt} onChange={(e) => setNextCallAt(e.target.value)}
                          // The picker itself is bounded, so an out-of-window date
                          // cannot be typed in either — the chips above are the fast
                          // path, this is the escape hatch, and both obey the window.
                          inputProps={{
                            min: o.dateWindow ? inDays(o.dateWindow.hardMin ? o.dateWindow.minDays : 0) : minLocal,
                            ...(o.dateWindow ? { max: inDays(o.dateWindow.maxDays) } : {}),
                          }}
                          style={{ background: '#fff' }}
                        />
                        {selected && belowFloor && (
                          <Box style={{
                            marginTop: 7, padding: '7px 9px', borderRadius: 7,
                            background: '#fff4e5', border: '1px solid #f0c27b',
                          }}>
                            <Typography style={{ fontSize: 11, color: '#8a5a00', fontWeight: 700 }}>
                              That is within {o.dateWindow.minDays} days — this looks like a HOT
                              follow-up. Switch to HOT, or carry on if they really did say this date.
                            </Typography>
                          </Box>
                        )}
                        {selected && aboveCeiling && (
                          <Box style={{
                            marginTop: 7, padding: '7px 9px', borderRadius: 7,
                            background: '#fdecea', border: '1px solid #f5b0ab',
                          }}>
                            <Typography style={{ fontSize: 11, color: '#b71c1c', fontWeight: 700 }}>
                              {o.label} call-backs must be within {o.dateWindow.maxDays} days.
                              Pick a nearer date, or choose a different status.
                            </Typography>
                          </Box>
                        )}
                        <Typography style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 6 }}>
                          {o.value === 'PRICE_ISSUE'
                            ? 'Leave blank if the rate they want is below what we can do — it goes to the pricing list instead.'
                            : 'This buyer jumps to the TOP of your call list that day — you don’t have to remember them.'}
                        </Typography>
                        </>
                        )}
                        {/* All three required, so the pricing queue can answer
                            "how much demand sits behind what rate gap". Inline
                            with the outcome for the same reason as the date. */}
                        {o.value === 'PRICE_ISSUE' && (
                          <Box style={{ marginTop: 10 }}>
                            <Typography style={{ fontSize: 11.5, fontWeight: 800, color: '#374151', marginBottom: 7 }}>
                              What exactly did they ask for? <span style={{ color: '#c62828' }}>*</span>
                            </Typography>
                            <FormControl fullWidth size="small" variant="outlined" style={{ background: '#fff', marginBottom: 8 }}>
                              <InputLabel>Product Category</InputLabel>
                              <Select
                                label="Product Category" value={priceCategory} onChange={(e) => setPriceCategory(e.target.value)}
                                // Same fix as SoftphoneCall.js's DISPOSITION_MENU_PROPS — this
                                // dialog is forced to zIndex 1500 (above RtoRiskLockPopup's lock
                                // overlay), but a Select's own dropdown Modal defaults to 1300
                                // and would otherwise render behind its parent dialog.
                                MenuProps={{ style: { zIndex: 1600 } }}
                              >
                                {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                              </Select>
                            </FormControl>
                            <Box display="flex" style={{ gap: 8 }}>
                              <TextField
                                fullWidth size="small" variant="outlined" type="number" label="Price wanted"
                                value={priceAsk} onChange={(e) => setPriceAsk(e.target.value)}
                                inputProps={{ min: 1 }} style={{ background: '#fff' }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ fontSize: 13, color: '#64748b' }}>₹</span></InputAdornment> }}
                              />
                              {/* Unit spelled out: a bare "5" is unreadable to
                                  whoever reviews the rate — 5 pieces and 5 boxes
                                  are different asks. */}
                              <TextField
                                fullWidth size="small" variant="outlined" type="number" label="Quantity"
                                value={priceQty} onChange={(e) => setPriceQty(e.target.value)}
                                inputProps={{ min: 1 }} style={{ background: '#fff' }}
                                InputProps={{ endAdornment: <InputAdornment position="end"><span style={{ fontSize: 11.5, color: '#64748b' }}>pcs</span></InputAdornment> }}
                              />
                            </Box>
                          </Box>
                        )}
                        {/* Remarks sits WITH the outcome, not at the foot of the
                            dialog. Stranded at the bottom it was far enough down
                            that agents would simply forget it existed — the same
                            failure that produced 9,378 junk "cb"/"ni" remarks on
                            the old form. */}
                        <TextField
                          fullWidth size="small" variant="outlined" multiline
                          error={selected && !!o.remarkRequired && !!remarkIssue && !!String(remark || '').trim()}
                          helperText={selected && o.remarkRequired ? (remarkIssue || ' ') : undefined}
                          label={o.value === 'COMPLAINT_RATE_SCHEME'
                            ? 'What is the complaint? *'
                            : o.remarkRequired ? 'What did the buyer say? *' : 'Remarks (optional)'}
                          placeholder={
                            o.value === 'OLD_STOCK' ? 'How much stock is left?'
                            : (o.value === 'WILL_PLACE_LATER' || o.value === 'WILL_PLACE_LATER_HOT') ? 'What did they commit to?'
                            : o.value === 'DENY_CONTINUE' ? 'Why are they stopping?'
                            : o.value === 'NO_NEED_UNKNOWN' ? 'Anything they hinted at?'
                            : ''
                          }
                          value={remark} onChange={(e) => setRemark(e.target.value)}
                          style={{ background: '#fff', marginTop: (o.dateMode !== 'none' || o.value === 'PRICE_ISSUE') ? 10 : 0 }}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          );
        })}

        {error && (
          <Typography style={{ fontSize: 12, color: '#c62828', fontWeight: 600, marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{
        padding: '10px 20px 16px', display: 'block',
        borderTop: '1px solid #eef1f4', background: '#fff',
      }}>
        <Button
          fullWidth variant="contained" disabled={submitDisabled} onClick={submit}
          startIcon={submitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 17 }} />}
          style={{
            // A disabled button still says WHY it's disabled (amber = something
            // to fill), rather than reading as a dead grey slab.
            background: submitting ? accent
              : !outcome ? 'linear-gradient(90deg,#cbd5e1,#e2e8f0)'
              : (dateMissing || priceMissing) ? 'linear-gradient(90deg,#f0a92b,#e0952a)'
              : `linear-gradient(90deg, ${accent}, ${accent}dd)`,
            color: !outcome ? '#64748b' : '#fff',
            fontWeight: 900, padding: '12px 0', fontSize: 14.5, borderRadius: 10,
            boxShadow: outcome ? `0 3px 10px ${accent}33` : 'none',
            textTransform: 'none',
          }}
        >
          {submitting ? 'Saving…'
            : !outcome ? 'Pick an outcome to continue'
            : subMissing ? 'Pick the exact status to continue'
            : dateMissing ? 'Set the date to continue'
            : aboveCeiling ? `Date must be within ${dateWindow.maxDays} days`
            : priceMissing ? 'Fill category, price & quantity'
            : remarkIssue ? 'Add a proper remark to continue'
            : `Log as "${meta.label}"`}
        </Button>
        {redial && (
          <>
            <Button
              fullWidth variant="text" size="small"
              disabled={redial.busy || submitting}
              onClick={redial.onClick}
              style={{ marginTop: 6, color: accent, fontWeight: 700, textTransform: 'none' }}
            >
              {redial.busy ? 'Redialing…' : '↻ Redial this number (cut by mistake)'}
            </Button>
            {redial.error && (
              <Typography style={{ fontSize: 11.5, color: '#c62828', fontWeight: 700, textAlign: 'center' }}>
                {redial.error}
              </Typography>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
